import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { getToken } from "@/lib/auth";
import type { Worker, WorkerCreateInput, WorkerFace } from "@/types/workers";

const API_BASE = ((import.meta.env.VITE_API_URL as string | undefined) ?? "").replace(/\/$/, "");

export function useWorkers() {
  return useQuery<Worker[]>({
    queryKey: ["workers"],
    queryFn: () => api<Worker[]>("/api/workers"),
  });
}

export function useCreateWorker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: WorkerCreateInput) =>
      api<Worker>("/api/workers", { method: "POST", body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workers"] });
    },
  });
}

export function useDeleteWorker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/api/workers/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workers"] });
    },
  });
}

export function usePatchWorker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<WorkerCreateInput> }) =>
      api<Worker>(`/api/workers/${id}`, { method: "PATCH", body: patch }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workers"] });
    },
  });
}

export function useWorkerFaces(workerId: string | null) {
  return useQuery<WorkerFace[]>({
    queryKey: ["worker-faces", workerId],
    queryFn: () => api<WorkerFace[]>(`/api/workers/${workerId}/faces`),
    enabled: Boolean(workerId),
  });
}

/** Register a face photo by URL + angle. */
export function useRegisterFace(workerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { photo_url: string; angle: string }) =>
      api<WorkerFace>(`/api/workers/${workerId}/faces`, { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["worker-faces", workerId] });
      qc.invalidateQueries({ queryKey: ["workers"] });
    },
  });
}

/** Upload a face photo file (multipart). */
export function useUploadFace(workerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, angle }: { file: File; angle: string }) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("angle", angle);
      const token = getToken();
      const url = `${API_BASE}/api/workers/${workerId}/faces/upload`;
      const res = await fetch(url, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        const text = await res.text();
        let detail = res.statusText;
        try {
          const j = JSON.parse(text);
          detail = j.detail ?? j.message ?? detail;
        } catch {
          if (text) detail = text;
        }
        throw new ApiError(res.status, detail);
      }
      return (await res.json()) as WorkerFace;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["worker-faces", workerId] });
      qc.invalidateQueries({ queryKey: ["workers"] });
    },
  });
}

/** C1-5 — remove one registered face photo (DELETE /api/workers/{id}/faces/{face_id}). */
export function useDeleteFace(workerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (faceId: string) =>
      api<void>(`/api/workers/${workerId}/faces/${faceId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["worker-faces", workerId] });
      qc.invalidateQueries({ queryKey: ["workers"] });
    },
  });
}

/** C1-5 — rebuild the worker's face embeddings from the stored photos.
 *  The route is `/faces/recompute` (backend/api/workers.py); the bare
 *  `/recompute` this used to call does not exist and always 404'd. */
export function useRecomputeFaces(workerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ status: string; count: number }>(`/api/workers/${workerId}/faces/recompute`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["worker-faces", workerId] });
      qc.invalidateQueries({ queryKey: ["workers"] });
    },
  });
}
